<template>
  <Teleport defer to="#chat-modal-root">
    <div class="pointer-events-auto">
      <div
        v-if="isVisible"
        class="absolute inset-0 z-50"
        aria-modal="true"
        role="dialog"
        @dragover.prevent
        @drop.prevent="handleDrop"
        @dragenter.prevent="handleDragEnter"
        @dragleave.prevent="handleDragLeave"
        @dragstart.prevent
        @dragend.prevent
      >
        <div
          class="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-200"
          :class="{ 'opacity-100': isDragOver, 'opacity-50': !isDragOver }"
          @click="handleClose"
          aria-hidden="true"
        ></div>
        
        <div class="flex h-full items-center justify-center px-[8px] pb-[40px] pt-[76px] text-center sm:items-center sm:px-0 sm:pt-0">
          <div
            class="relative z-10 w-full max-w-md mx-4 p-8 bg-white rounded-lg shadow-2xl border-2 border-dashed transition-all duration-200"
            :class="{
              'border-blue-500 bg-blue-50': isDragOver,
              'border-gpx_gray-800 bg-white': !isDragOver
            }"
          >
            <div class="text-center mb-4">
              <div
                class="mx-auto w-16 h-16 rounded-full flex items-center justify-center transition-colors duration-200"
                :class="{
                  'bg-blue-100 text-blue-600': isDragOver,
                  'bg-gpx_gray-900 text-gpx_gray-700': !isDragOver
                }"
              >
                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
            </div>
            
            <div class="text-center">
              <h3
                class="text-lg font-semibold mb-2 transition-colors duration-200"
                :class="{
                  'text-blue-700': isDragOver,
                  'text-gpx_gray-200': !isDragOver
                }"
              >
                {{ isDragOver ? 'Drop files here' : 'Drag files here' }}
              </h3>
              <p class="text-sm text-gpx_gray-700 mb-4">
                Drop files to upload them (TXT, CSV, MD, JSON, XML, YAML, JPG, PNG, GIF, WEBP, PDF)
              </p>
              <p class="text-xs text-gpx_gray-700">
                Maximum 5 files, 32MB each
              </p>
            </div>
            
            <button
              @click="handleClose"
              class="absolute top-2 right-2 p-1 text-gpx_gray-700 hover:text-gpx_gray-600 transition-colors"
              title="Cancel"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const emit = defineEmits(['files-dropped']);

const isVisible = ref(false);
const isDragOver = ref(false);

const handleGlobalDragEnter = (event) => {
  if (event.dataTransfer.types.includes('Files') && !isVisible.value) {
    isVisible.value = true;
  }
};

const handleGlobalDragLeave = (event) => {
  if (!event.relatedTarget || event.relatedTarget === document.documentElement) {
    setTimeout(() => {
      if (!isDragOver.value) {
        isVisible.value = false;
      }
    }, 100);
  }
};

const handleGlobalDragEnd = (event) => {
  setTimeout(() => {
    if (!isDragOver.value) {
      isVisible.value = false;
    }
  }, 100);
};

const handleDragEnter = (event) => {
  isDragOver.value = true;
};

const handleDragLeave = (event) => {
  if (!event.currentTarget.contains(event.relatedTarget)) {
    isDragOver.value = false;
    
    setTimeout(() => {
      if (!isDragOver.value) {
        isVisible.value = false;
      }
    }, 100);
  }
};

const handleDrop = (event) => {
  isDragOver.value = false;
  
  const files = Array.from(event.dataTransfer.files || []);
  
  if (files.length > 0) {
    emit('files-dropped', files);
  }
  
  handleClose();
};

const handleClose = () => {
  isDragOver.value = false;
  isVisible.value = false;
};

onMounted(() => {
  document.addEventListener('dragenter', handleGlobalDragEnter);
  document.addEventListener('dragleave', handleGlobalDragLeave);
  document.addEventListener('dragend', handleGlobalDragEnd);
});

onUnmounted(() => {
  document.removeEventListener('dragenter', handleGlobalDragEnter);
  document.removeEventListener('dragleave', handleGlobalDragLeave);
  document.removeEventListener('dragend', handleGlobalDragEnd);
});
</script> 